import { useState, useEffect } from "react";
import {
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
} from "@mui/material";
import {
  ShepDataGridPro,
  ShepTooltip,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import { Close as CloseIcon } from "@mui/icons-material";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCrossReferenceSummarySelectedTab,
  selectSelectedConnectionPair,
} from "@/features/maintenance/maintenanceSlice";
import "./AddCrossReferencePopup.css";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import UpArrow from "@/assets/img/icons/ArrowUp/Line.svg?react";
import DownArrow from "@/assets/img/icons/ArrowDown/Line.svg?react";
import Edit from "@/assets/img/icons/Edit/Line.svg?react";


const catalogTypeNames = [
  "Customer",
  "Item",
  "Item UOM",
  "Warehouse",
  "Ship To",
  "Carrier",
  "Allowance/Charge",
];

const EditPrioritiesPopup = ({
    onClose,
    crossReferenceMappingId,
}) => {
  const dispatch = useDispatch();
  const catalogType = useSelector(selectCrossReferenceSummarySelectedTab);
  const connectionPair = useSelector(selectSelectedConnectionPair);
  const apiRef = useGridApiRef();

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [newPriorityValue, setNewPriorityValue] = useState(0);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const [pendingNewPriorityConfirmation, setPendingNewPriorityConfirmation] = useState(true)

  const saveUpdatedCrossReferences = async () => {
    try {
      const payload = rows.map(row => {
        return {
          CrossReferenceMappingId: row.CrossReferenceMappingId,
          Priority: row.Priority
        }
      });
      await axios.post(
        "/crossreference/linked-cross-references",
        payload,
        {
          params: {
            connectionPairId: connectionPair.id,
          },
        },
      );
      fetchLinkedCrossReferences(crossReferenceMappingId, connectionPair.id);
    } catch (ex) {
      console.log(ex.response.data);
      dispatch(
          createAlert({
              status: "Error",
              alertType: "single",
              message: `Error fetching linked cross references ${ex.response.data}`,
          })
      );
    }
  }

  const fetchLinkedCrossReferences = async (crossReferenceMappingId, connectionPairId) => {
    try {
        setIsLoading(true);
        const response = await axios.get("/crossreference/linked-cross-references", {
            params: {
                crossReferenceMappingId,
                connectionPairId
            },
        });
        setRows(
            response.data
            .map((row, i) => {
                row.id = i;
                return row;
            }));
    } catch (ex) {
        console.log(ex.response.data);
        dispatch(
            createAlert({
                status: "Error",
                alertType: "single",
                message: `Error fetching linked cross references ${ex.response.data}`,
            })
        );
    } finally {
        setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedCrossReferences(crossReferenceMappingId, connectionPair.id)
  }, [crossReferenceMappingId, connectionPair.id]);

  useEffect(() => {
    if (!pendingNewPriorityConfirmation) {
      updatePriority(selectedRowId, newPriorityValue)
    }
  }, [newPriorityValue, selectedRowId, pendingNewPriorityConfirmation]);

  const processSetPriority = (rowId) => {
    setSelectedRowId(rowId)
    setPendingNewPriorityConfirmation(true);
    dispatch(
      createAlert({
        status: "Info",
        alertType: "result",
        message: 'Choose insert priority:',
        buttonLabel: "Confirm",
        onButtonClick: () => {
          setPendingNewPriorityConfirmation(false);
        },
        intValue: newPriorityValue,
        setIntValue: (val) => {
          setNewPriorityValue(val)
        }
      })
    )
  }

  const updatePriority = (rowId, newPriority) => {
    const selectedRow = rows.find(row => row.id === rowId);
    const maxPriority = rows.length - 1;
    const increment = selectedRow.Priority - newPriority;
    const originalPriority = selectedRow.Priority;

    if (maxPriority === 0) {
      dispatch(
        createAlert({
          status: "Info",
          alertType: "single",
          message: `Cannot change priority when there is only one mapping`,
        }),
      );
      return;
    } else if (newPriority < 0 || newPriority > maxPriority) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: `Error! Priority must be between 0 and ${maxPriority} (inclusive)`
        })
      )
      return;
    }

    const updatedRows = rows.map(row => {
      var {
        Priority: priorPriority,
        id: currentRowId,
      } = row;
      if (rowId === currentRowId) {
        return  {
          ...row,
          Priority: newPriority,
        }
      } else if (increment < 0 && row.Priority <= newPriority && row.Priority > originalPriority) {
        return {
          ...row,
          Priority: priorPriority - 1,
        }
      } else if (increment > 0 && row.Priority >= newPriority && row.Priority < originalPriority) {
        return {
          ...row,
          Priority: priorPriority + 1,
        }
      } else {
        return {
          ...row,
        };
      }
    });

    setRows(updatedRows);
  }

    const columns = [
        {
            headerName: "Source Catalog",
            field: "SourceCatalogDescription",
            width: 300,
            headerSegment: 3,
        },
        {
            headerName: "Target Catalog",
            field: "TargetCatalogDescription",
            width: 300,
            headerSegment: 3,
        },
        {
            headerName: "Priority",
            field: "Priority",
            width: 300,
            headerSegment: 3,
        },
        {
            headerName: "actions",
            field: "actions",
            width: 120,
            renderCell: ({id, row: {Priority}}) => (
                <>
                    <IconButton onClick={() => updatePriority(id, Priority - 1)}>
                        <UpArrow />
                    </IconButton>
                    <IconButton onClick={() => processSetPriority(id)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => updatePriority(id, Priority + 1)}>
                        <DownArrow />
                    </IconButton>
                </>
            ),
            sortable: false,
            filterable: false,
            resizable: false,
        },
    ];
  return (
    <div>
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
        }}
      >
        <div style={{ display: "flex" }}>
          <div style={{ fontWeight: "var(--font-semi-bold)" }}>
            {"Edit priorities in - " + catalogTypeNames[catalogType]}
          </div>
        </div>
        <IconButton
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
          onClick={() => onClose()}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="dialogContent" style={{ overflow: "hidden" }}>
        <Box
          sx={{
            flex: "auto",
            marginLeft: "5px",
            width: "100%",
            backgroundColor: "var(--disabled)",
          }}
        >
          <Box marginTop={"20px"} minHeight={"1000px"}>
            <ShepDataGridPro
              apiRef={apiRef}
              isDataLoaded={!isLoading}
              rows={rows}
              columns={columns}
              columnHeaderHeight={78}
              rowHeight={100}
              pagination
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15, page: 0 },
                },
                pinnedColumns: {
                  right: ["actions"],
                },
              }}
              sortModel={[{ field: "Priority", sort: "asc" }]}
              whiteHeader={true}
              checkboxSelection={false}
              disableMultipleRowSelection={false}
              disableColumnMenu={true}
              disableColumnFilter={true}
              disableColumnReorder={true}
              hideFooterSelectedRowCount={true}
              disableRowSelectionOnClick={false}
              slots={{
                baseTooltip: ShepTooltip,
                pagination: ShepPagination,
              }}
              sx={{
                color: "var(--text-primary)",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "44px",
                borderTopRightRadius: "44px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid:hover": {
                  backgroundColor: "inherit",
                },
                "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                  opacity: 0,
                  borderBottom: "none",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                  {
                    outline: "none",
                  },
                "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                  {
                    opacity: 1,
                  },
                "& .MuiDataGrid-cellCheckbox": {
                  borderLeft: "8px solid transparent",
                },
                "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                  opacity: 1,
                  borderLeft: "8px solid var(--primary-main)",
                  borderTopLeftRadius: "4px",
                  borderBottomLeftRadius: "4px",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                  {
                    borderBottomLeftRadius: "4px",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "var(--primary-pale)",
                    borderBottom: "none",
                    borderLeft: "none",
                  },
                "& .MuiDataGrid-pinnedColumnHeaders.MuiDataGrid-pinnedColumnHeaders--right":
                  {
                    display: "none",
                  },
                "& .MuiDataGrid-columnHeader[data-field='ErpConnectionName']": {
                  paddingLeft: "45px",
                },
                "& .MuiDataGrid-cell[data-field='ErpConnectionName']": {
                  paddingLeft: "45px",
                },
                ...(Object.keys(connectionPair).length === 0
                  ? {
                      "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-footerContainer, & .MuiDataGrid-scrollbar, & .MuiDataGrid-virtualScroller, & .gridHeaderSegment":
                        { display: "none" },
                      background: "transparent",
                      minHeight: "600px",
                    }
                  : {}),
              }}
            />
          </Box>
        </Box>
        <Box style={{ display: "flex", justifyContent: "flex-end" }}>
          <ShepButton variant="primary" onClick={saveUpdatedCrossReferences}>
            Save Changes
          </ShepButton>
        </Box>
      </DialogContent>
    </div>
  );
};

export default EditPrioritiesPopup;
